import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';


interface StateItem {
  id: number;
  state_name: string;
  state_code: string;
  allowed: boolean;
}

const StateList = () => {
  const [states, setStates] = useState<StateItem[]>([]);
  const [editState, setEditState] = useState<StateItem | null>(null);
  const navigate = useNavigate();

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    navigate('/login');
  };

  // Fetch states from the API
  const fetchStates = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL_API}/admin/getStatelist`);
      if (response.data) {
        setStates(response.data.data);
      } else {
        toast.error('Failed to fetch states.');
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      toast.error('Error fetching states.');
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  // Handle input changes for editing states
  const handleEditStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editState) {
      const { name, value, type } = e.target;
      setEditState((prev) => ({
        ...prev!,
        [name]: type === 'checkbox' ? e.target.checked : value,
      }));
    }
  };

  // Update an existing state
  const handleUpdateState = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editState) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL_API}/admin/updateState/${editState.id}`,
          editState
        );
        if (response.data.status) {
          toast.success('State updated successfully!');
          setEditState(null);
          fetchStates();
        } else {
          toast.error('Failed to update state.');
        }
      } catch (error) {
        console.error('Error updating state:', error);
        toast.error('Error updating state.');
      }
    }
  };

  return (
    <div className="ml-0 p-4 bg-white">
      <h2 className="text-2xl font-semibold mb-4 text-black">State Management</h2>
      <hr className="p-5" />

      {/* Edit Existing State Form */}
      {editState && (
        <div className="mb-8">
          <h3 className="text-xl text-black">Edit State</h3>
          <form onSubmit={handleUpdateState} className="space-y-4">
            <div>
              <label className="text-black">State Name</label>
              <input
                type="text"
                name="state_name"
                value={editState.state_name}
                onChange={handleEditStateChange}
                className="border rounded p-2 w-full"
              />
            </div>
            <div>
              <label className="text-black">State Code</label>
              <input
                type="text"
                name="state_code"
                value={editState.state_code}
                onChange={handleEditStateChange}
                className="border rounded p-2 w-full"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-black">Allowed</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="allowed"
                  checked={editState?.allowed}
                  onChange={handleEditStateChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer-checked:bg-[#228BE6] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              </label>
            </div>

            <button type="submit" className="bg-[#228BE6] text-black py-2 px-4 rounded">
              Update State
            </button>
            <button
              type="button"
              onClick={() => setEditState(null)}
              className="bg-red-500 text-black py-2 px-4 rounded ml-2"
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Display States */}
      <div>
        {states.length > 0 ? (
          <table className="table-auto w-full text-black text-center">
            <thead>
              <tr>
                <th className="border px-4 py-2">S.No.</th>
                <th className="border px-4 py-2">State Name</th>
                <th className="border px-4 py-2">State Code</th>
                <th className="border px-4 py-2">Allowed</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {states.map((state, index) => (
                <tr key={state.id}>
                  <td className="border px-4 py-2">{index + 1}</td>
                  <td className="border px-4 py-2">{state.state_name}</td>
                  <td className="border px-4 py-2">{state.state_code}</td>
                  <td className="border px-4 py-2">
                    {state.allowed ? (
                      <span className="text-green-600">✅ Allowed</span>
                    ) : (
                      <span className="text-rose-600">❌ Not Allowed</span>
                    )}
                  </td>
                  <td className="border px-4 py-2">
                    <button
                      onClick={() => setEditState(state)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black py-1 px-3 rounded"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-black">No states available at the moment.</p>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default StateList;
